import React from 'react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  ComposedChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip,
  Cell, ReferenceLine, ReferenceArea,
} from 'recharts';
import { seriesColor } from '../lib/palette';

/* ─── series colour ──────────────────────────────────────────────────────
   The page chrome is monochrome; the data is not. Every series carries the
   colour the backend attached to it (team colour, tyre compound, driver),
   so a McLaren reads as a McLaren here exactly as it does on the timing
   tower. Fallback only when a payload arrives bare. */
const TICK = { fill: '#8c8c8c', fontFamily: "'Geist Mono', ui-monospace, monospace", fontSize: 10 };
const GRID = { stroke: 'rgba(255,255,255,0.07)' };
const AXIS = { stroke: 'rgba(255,255,255,0.16)' };

function ChartTip({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="pv-tip">
      <div className="pv-tip-label">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="pv-tip-row">
          <span className="pv-tip-dot" style={{ background: p.color || p.stroke || '#fff' }} />
          <span>{p.name}</span>
          <strong>{formatter ? formatter(p.value, p.name) : (typeof p.value === 'number' ? p.value.toFixed(3) : p.value)}</strong>
        </div>
      ))}
    </div>
  );
}

/**
 * Colour + dash for series `i` of a list. Colour comes from the payload;
 * the dash is only spent when a colour REPEATS (team-mates share a hue), so
 * twenty drivers do not become twenty dashed lines for no reason.
 */
/* The second of a pair is drawn dashed so two drivers never share a solid
   line; the legend has to say so too, or the swatch lies. */
const PAIR_DASH = '7 4';

function seriesStyle(list, i) {
  const colour = seriesColor(list[i], i);
  const repeats = list.slice(0, i).filter((d, j) => seriesColor(d, j) === colour).length;
  return { colour, dash: repeats === 0 ? undefined : '7 4' };
}

function SeriesLegend({ items }) {
  if (!items?.length) return null;
  return (
    <div className="pv-legend">
      {items.map((it, i) => (
          <span key={it.name + i} className="pv-legend-item">
            <span className="pv-swatch" style={{
              borderTopColor: seriesColor(it, i),
              borderTopStyle: it.dash ? 'dashed' : 'solid',
            }} />
            {it.name}
          </span>
        ))}
    </div>
  );
}

const fmtLapTime = (seconds) => {
  if (seconds == null) return '';
  const m = Math.floor(seconds / 60);
  const s = (seconds % 60).toFixed(3);
  return m > 0 ? `${m}:${s.padStart(6, '0')}` : `${s}s`;
};

const wrap = (children) => <div className="pv-chart">{children}</div>;

/** Black or white text, whichever survives on `bg` — for labels sitting
    inside a coloured block (stint chips). */
function readableInk(bg) {
  const m = /#([0-9a-f]{6})/i.exec(bg || '');
  if (!m) return '#000';
  const n = parseInt(m[1], 16);
  const lum = (0.299 * ((n >> 16) & 255)) + (0.587 * ((n >> 8) & 255)) + (0.114 * (n & 255));
  return lum > 150 ? '#000' : '#fff';
}

/* ═══ Fastest laps — horizontal bars ═══════════════════════════════════ */
export const FastestChart = ({ data, pole_time, pole_driver }) => {
  if (!data?.length) return null;
  const chartData = data.map((d) => ({ driver: d.driver, delta: d.delta ?? 0, color: d.color }));
  return wrap(
    <>
      <div className="pv-chart-sub">Pole — {pole_driver} · {pole_time}</div>
      <div className="pv-chart-h">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical"
            margin={{ top: 5, right: 64, left: 8, bottom: 5 }}>
            <CartesianGrid {...GRID} horizontal={false} />
            <XAxis type="number" tick={TICK} axisLine={AXIS} tickLine={false}
              tickFormatter={(v) => `+${v.toFixed(3)}s`} />
            <YAxis type="category" dataKey="driver" tick={{ ...TICK, fontSize: 11 }}
              axisLine={false} tickLine={false} width={48} />
            <Tooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }}
              content={<ChartTip formatter={(v) => `+${Number(v).toFixed(3)}s`} />} />
            <Bar dataKey="delta" name="Delta" barSize={15}>
              {chartData.map((d, i) => <Cell key={i} fill={seriesColor(d, i)} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};

/* ═══ Multi-series line helper ═════════════════════════════════════════ */
function MultiLine({ dataKey, title, yLabel, xLabel, formatter, domain, reversed, extra }) {
  const drivers = title.drivers || [];
  return wrap(
    <>
      {title.sub && <div className="pv-chart-sub">{title.sub}</div>}
      <SeriesLegend items={drivers.map((d, i) => {
        const st = seriesStyle(drivers, i);
        return { name: d.name, color: st.colour, dash: st.dash };
      })} />
      <div className="pv-chart-h">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart margin={{ top: 8, right: 16, left: 4, bottom: 4 }}>
            <CartesianGrid {...GRID} />
            <XAxis dataKey={title.xKey} type="number" tick={TICK} axisLine={AXIS} tickLine={false}
              allowDuplicatedCategory={false}
              label={xLabel ? { value: xLabel, position: 'insideBottomRight', offset: -4, ...TICK } : undefined}
              tickFormatter={title.xFmt} domain={title.xDomain} />
            <YAxis tick={TICK} axisLine={false} tickLine={false} domain={domain} reversed={reversed}
              tickFormatter={yLabel?.fmt}
              label={yLabel?.text ? { value: yLabel.text, angle: -90, position: 'centerLeft', dx: -26, ...TICK } : undefined} />
            <Tooltip content={<ChartTip formatter={formatter} />} />
            {drivers.map((d, i) => {
              const st = seriesStyle(drivers, i);
              return (
                <Line key={d.name} data={d.data} dataKey={dataKey} name={d.name}
                  stroke={st.colour} strokeDasharray={st.dash}
                  dot={false} strokeWidth={1.7}
                  /* Recharts' draw-on animation leaves stroke-dasharray at
                     "<pathLength> <pathLength>" after it finishes, so a solid
                     line still reports a dash to anything that reads the DOM —
                     and it re-fires on every run. Data should be readable the
                     frame it arrives. */
                  isAnimationActive={false} />
              );
            })}
            {extra}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}

export const LapsChart = ({ drivers }) => {
  if (!drivers?.length) return null;
  return <MultiLine dataKey="time" title={{ drivers, xKey: 'lap', xLabel: 'Lap' }}
    yLabel={{ text: 'Lap time', fmt: fmtLapTime }} formatter={fmtLapTime} />;
};

export const SpeedChart = ({ drivers, xKey, xLabel }) => {
  if (!drivers?.length) return null;
  return <MultiLine dataKey="speed"
    title={{ drivers, xKey, xLabel, xFmt: xKey === 'time' ? fmtLapTime : undefined }}
    yLabel={{ text: 'Speed (km/h)' }}
    formatter={(v, name) => (String(name).toLowerCase().includes('speed') ? `${Number(v).toFixed(0)} km/h` : v)} />;
};

export const PositionsChart = ({ drivers, safety_car }) => {
  if (!drivers?.length) return null;
  return <MultiLine dataKey="position"
    title={{ drivers, xKey: 'lap', xLabel: 'Lap' }}
    yLabel={{ text: 'Position' }}
    domain={[1, 20]} reversed
    formatter={(v) => `P${v}`}
    extra={<>
      {safety_car?.map((sc, i) => (
        <ReferenceArea key={i} x1={sc.start} x2={sc.end}
          fill="rgba(255,255,255,0.10)"
          label={{ value: sc.type, fill: '#8c8c8c', fontSize: 10 }} />
      ))}
    </>} />;
};

export const RaceTraceChart = ({ drivers }) => {
  if (!drivers?.length) return null;
  return <MultiLine dataKey="cumulative"
    title={{ drivers, xKey: 'lap', xLabel: 'Lap' }}
    yLabel={{ text: 'Race trace (s)' }}
    formatter={(v) => `${Number(v).toFixed(2)}s`} />;
};

/* ═══ Delta — two speeds + the time gap ════════════════════════════════ */
export const DeltaChart = ({ data, driver1, driver2 }) => {
  if (!data?.length) return null;
  const c1 = seriesColor(driver1, 0);
  const c2 = seriesColor(driver2, 1);
  return wrap(
    <>
      <SeriesLegend items={[
        { name: `${driver1.name} speed`, color: c1 },
        { name: `${driver2.name} speed`, color: c2, dash: PAIR_DASH },
        { name: 'Delta', color: '#ffffff', dash: '3 3' },
      ]} />
      <div className="pv-chart-h">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 16, left: 4, bottom: 4 }}>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="distance" tick={TICK} axisLine={AXIS} tickLine={false}
              label={{ value: 'Distance (m)', position: 'insideBottomRight', offset: -4, ...TICK }} />
            <YAxis yAxisId="speed" tick={TICK} axisLine={false} tickLine={false}
              label={{ value: 'Speed (km/h)', angle: -90, position: 'centerLeft', dx: -26, ...TICK }} />
            <YAxis yAxisId="delta" orientation="right" tick={TICK} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTip formatter={(v, name) => (String(name).includes('Speed') ? `${Number(v).toFixed(0)} km/h` : `${Number(v).toFixed(3)}s`)} />} />
            <Line yAxisId="speed" dataKey="speed_d1" name={`${driver1.name} speed`}
              stroke={c1} dot={false} strokeWidth={1.6} isAnimationActive={false} />
            <Line yAxisId="speed" dataKey="speed_d2" name={`${driver2.name} speed`}
              stroke={c2} strokeDasharray={PAIR_DASH} dot={false} strokeWidth={1.6}
              isAnimationActive={false} />
            <Line yAxisId="delta" dataKey="delta" name="Delta"
              stroke="#ffffff" strokeDasharray="3 3" dot={false} strokeWidth={1}
              isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};

/* ═══ Full telemetry — stacked channels ════════════════════════════════ */
const TelPanel = ({ data, d1Key, d2Key, label, driver1, driver2, yDomain }) => {
  const c1 = seriesColor(driver1, 0);
  const c2 = seriesColor(driver2, 1);
  return (
    <div style={{ position: 'relative', width: '100%', height: 96 }}>
      <span style={{ position: 'absolute', top: 2, right: 12, fontSize: '0.55rem', color: '#8c8c8c', zIndex: 1, fontFamily: "'Geist Mono', monospace", textTransform: 'uppercase', letterSpacing: 1 }}>{label}</span>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 10, left: -6, bottom: 2 }} syncId="tel">
          <CartesianGrid {...GRID} />
          <XAxis dataKey="distance" tick={false} axisLine={false} />
          <YAxis tick={{ ...TICK, fontSize: 8 }} width={44} axisLine={false} tickLine={false} domain={yDomain} />
          <Tooltip content={<ChartTip />} />
          <Line dataKey={d1Key} name={driver1.name} stroke={c1} dot={false}
            strokeWidth={1.4} isAnimationActive={false} />
          <Line dataKey={d2Key} name={driver2.name} stroke={c2} strokeDasharray={PAIR_DASH}
            dot={false} strokeWidth={1.4} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export const TelemetryChart = ({ data, driver1, driver2 }) => {
  if (!data?.length) return null;
  const panels = [
    { d1: 'd1_speed', d2: 'd2_speed', label: 'Speed' },
    { d1: 'd1_rpm', d2: 'd2_rpm', label: 'RPM' },
    { d1: 'd1_gear', d2: 'd2_gear', label: 'Gear', domain: [0, 9] },
    { d1: 'd1_throttle', d2: 'd2_throttle', label: 'Throttle', domain: [0, 100] },
    { d1: 'd1_brake', d2: 'd2_brake', label: 'Brake', domain: [-1, 1] },
    { d1: 'd1_drs', d2: 'd2_drs', label: 'DRS', domain: [-1, 1] },
  ];
  return wrap(
    <>
      <SeriesLegend items={[
        { name: `${driver1.name} · ${driver1.lap}`, i: 0 },
        { name: `${driver2.name} · ${driver2.lap}`, i: 1 },
      ]} />
      {panels.map((p) => (
        <TelPanel key={p.label} data={data} d1Key={p.d1} d2Key={p.d2} label={p.label}
          driver1={driver1} driver2={driver2} yDomain={p.domain} />
      ))}
      <div style={{ position: 'relative', width: '100%', height: 150 }}>
        <span style={{ position: 'absolute', top: 2, right: 12, fontSize: '0.55rem', color: '#8c8c8c', zIndex: 1, fontFamily: "'Geist Mono', monospace", textTransform: 'uppercase', letterSpacing: 1 }}>Delta</span>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 4, right: 10, left: -6, bottom: 18 }} syncId="tel">
            <CartesianGrid {...GRID} />
            <XAxis dataKey="distance" tick={TICK} axisLine={AXIS} tickLine={false}
              label={{ value: 'Distance (m)', position: 'insideBottomRight', offset: -4, ...TICK }} />
            <YAxis tick={{ ...TICK, fontSize: 8 }} width={44} axisLine={false} tickLine={false}
              tickFormatter={(v) => `${Math.abs(v).toFixed(1)}s`} />
            <Tooltip content={<ChartTip formatter={(v) => `${Number(v).toFixed(3)}s`} />} />
            <Area dataKey="delta" name={`${driver1.name} ahead ↑ · ${driver2.name} ahead ↓`}
              fill="rgba(255,255,255,0.07)" stroke="#ffffff" strokeWidth={1} dot={false}
              isAnimationActive={false} />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.25)" strokeDasharray="3 3" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};

/* ═══ Strategy — stint blocks, mono with compound letters ═══════════════ */
const COMPOUND_FALLBACK = {
  SOFT: '#ff3b30', MEDIUM: '#ffd60a', HARD: '#f2f2f7',
  INTERMEDIATE: '#30d158', WET: '#0a84ff',
  HYPERSOFT: '#ff2d92', ULTRASOFT: '#bf5af2', SUPERSOFT: '#ff9f0a',
  SUPERHARD: '#8e8e93', UNKNOWN: '#636366',
};

export const StrategyChart = ({ drivers, compound_colors }) => {
  if (!drivers?.length) return null;
  const compoundFill = { ...COMPOUND_FALLBACK, ...(compound_colors || {}) };
  const rowH = 26;
  const totalH = drivers.length * rowH + 64;
  const maxLap = Math.max(...drivers.flatMap((d) => d.stints?.map((s) => s.start + s.length) || [0]), 1);
  const compounds = [...new Set(drivers.flatMap((d) => (d.stints || []).map((s) => s.compound)))];

  return wrap(
    <>
      <SeriesLegend items={compounds.map((c) => ({ name: c, color: compoundFill[c] || '#8e8e93' }))} />
      <div className="pv-svg">
        <svg viewBox={`0 0 800 ${totalH}`} role="img"
          aria-label="Tyre stints per driver, laps on the horizontal axis">
          {Array.from({ length: Math.ceil(maxLap / 10) + 1 }, (_, i) => i * 10).map((lap) => (
            <text key={lap} x={64 + (lap / maxLap) * 716} y={totalH - 24}
              fill="#8c8c8c" fontSize="10" fontFamily="'Geist Mono', monospace" textAnchor="middle">{lap}</text>
          ))}
          <text x={400} y={totalH - 6} fill="#6f6f6f" fontSize="9"
            fontFamily="'Geist Mono', monospace" textAnchor="middle">LAP</text>
          {drivers.map((d, di) => (
            <g key={d.name}>
              <text x={56} y={22 + di * rowH + rowH / 2 + 4} fill="#ffffff" fontSize="10"
                fontFamily="'Geist Mono', monospace" textAnchor="end">{d.name}</text>
              {(d.stints || []).map((s, si) => (
                <g key={si}>
                  <rect
                    x={64 + (s.start / maxLap) * 716}
                    y={22 + di * rowH + 3}
                    width={Math.max(2, (s.length / maxLap) * 716)}
                    height={rowH - 8}
                    fill={compoundFill[s.compound] || s.color || '#8e8e93'}
                    stroke="#000" strokeWidth={1} />
                  {(s.length / maxLap) * 716 > 26 && (
                    <text
                      x={64 + (s.start / maxLap) * 716 + ((s.length / maxLap) * 716) / 2}
                      y={22 + di * rowH + rowH / 2 + 3}
                      fill={readableInk(compoundFill[s.compound] || '#8e8e93')}
                      fontSize="9" fontWeight="700" textAnchor="middle"
                      fontFamily="'Geist Mono', monospace">
                      {(s.compound || '?')[0]}
                    </text>
                  )}
                </g>
              ))}
            </g>
          ))}
        </svg>
      </div>
      {compound_colors && (
        <div className="pv-legend" style={{ marginTop: 8 }}>
          {Object.keys(compound_colors).filter((c) => compounds.includes(c)).map((c) => (
            <span key={c} className="pv-legend-item">
              <span className="pv-tip-dot" style={{ background: compoundFill[c] || '#8e8e93' }} />
              {c}
            </span>
          ))}
        </div>
      )}
    </>
  );
};

/* ═══ Battles ══════════════════════════════════════════════════════════ */
export const BattlesChart = ({ data }) => {
  if (!data?.length) return null;
  return wrap(
    <div className="pv-chart-h">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 10, right: 16, left: 4, bottom: 4 }}>
          <CartesianGrid {...GRID} />
          <XAxis dataKey="driver" tick={{ ...TICK, fontSize: 11 }} axisLine={AXIS} tickLine={false} />
          <YAxis tick={TICK} allowDecimals={false} axisLine={false} tickLine={false} />
          <Tooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }} content={<ChartTip />} />
          <Bar dataKey="score" name="Wins" barSize={38}>
            {data.map((d, i) => <Cell key={i} fill={seriesColor(d, i)} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ═══ Cornering ════════════════════════════════════════════════════════ */
const ACTION_FILL = { 'Full Throttle': '#30d158', Cornering: '#8e8e93', Brake: '#ff3b30' };

export const CorneringChart = ({ speed_data, driver1, driver2, speed_text, distance_range, action_colors }) => {
  if (!speed_data?.length) return null;
  const c1 = seriesColor(driver1, 0);
  const c2 = seriesColor(driver2, 1);
  const span = (distance_range?.[1] ?? 0) - (distance_range?.[0] ?? 0) || 1;
  const actionFill = action_colors || ACTION_FILL;
  return wrap(
    <>
      {speed_text && <div className="pv-chart-sub">{speed_text}</div>}
      <SeriesLegend items={[
        { name: driver1.name, color: c1 },
        { name: driver2.name, color: c2, dash: PAIR_DASH },
      ]} />
      <div className="pv-chart-h">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={speed_data} margin={{ top: 8, right: 16, left: 4, bottom: 4 }}>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="distance" tick={TICK} domain={distance_range} axisLine={AXIS} tickLine={false} />
            <YAxis tick={TICK} axisLine={false} tickLine={false}
              label={{ value: 'Speed (km/h)', angle: -90, position: 'centerLeft', dx: -26, ...TICK }} />
            <Tooltip content={<ChartTip formatter={(v) => `${Number(v).toFixed(0)} km/h`} />} />
            <Line dataKey="speed_d1" name={driver1.name} stroke={c1} dot={false}
              strokeWidth={1.7} isAnimationActive={false} />
            <Line dataKey="speed_d2" name={driver2.name} stroke={c2} strokeDasharray={PAIR_DASH}
              dot={false} strokeWidth={1.7} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
        {[driver1, driver2].map((d) => (
          <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="pv-legend-item" style={{ width: 56, flex: 'none' }}>{d.name}</span>
            <div style={{ display: 'flex', flex: 1, height: 10, background: '#161616' }}>
              {(d.actions || []).map((seg, i) => (
                <div key={i} title={`${seg.action}: ${Math.round(seg.width)}m`}
                  style={{ width: `${(seg.width / span) * 100}%`, background: ACTION_FILL[seg.action] || action_colors?.[seg.action] || '#767676' }} />
              ))}
            </div>
          </div>
        ))}
        <div className="pv-legend">
          {Object.keys(actionFill).map((name) => (
            <span key={name} className="pv-legend-item">
              <span className="pv-tip-dot" style={{ background: actionFill[name] }} />
              {name}
            </span>
          ))}
        </div>
      </div>
    </>
  );
};

/* ═══ Tables ═══════════════════════════════════════════════════════════ */
const TableScroll = ({ label, children }) => (
  <div className="pv-tablewrap" tabIndex={0} role="region" aria-label={label}>
    {children}
  </div>
);

export const DataTable = ({ columns, rows, column_keys }) => {
  if (!rows?.length) return null;
  const keys = column_keys || columns;
  return (
    <TableScroll label="Results table">
      <table className="table">
        <thead><tr>{columns.map((c, i) => <th key={i}>{c}</th>)}</tr></thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>{keys.map((k, ki) => <td key={ki}>{row[k] ?? '—'}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
};

export const ScheduleTable = ({ events }) => {
  if (!events?.length) return null;
  return (
    <TableScroll label="Season schedule">
      <table className="table">
        <thead><tr><th>Event</th><th>Venue</th><th>Date</th><th>Format</th></tr></thead>
        <tbody>
          {events.map((e, i) => (
            <tr key={i}>
              <td>{e.name}</td>
              <td className="pv-venue">{e.location || '—'}</td>
              <td>{e.date}</td>
              <td>{e.format}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
};

export const EventInfo = ({ fields }) => {
  if (!fields?.length) return null;
  return (
    <div className="pv-info">
      {fields.map((f, i) => (
        <div key={i} className="pv-info-row">
          <span className="pv-info-key">{f.key}</span>
          <span className="pv-info-val">{f.value}</span>
        </div>
      ))}
    </div>
  );
};

/* ═══ Info cards ═══════════════════════════════════════════════════════ */
export const TrackInfo = ({ data }) => {
  if (!data) return null;
  const ts = data.track_stats || {};
  const rows = [
    ts.official_name && ['Circuit', ts.official_name],
    data.location && ['Location', `${data.location}, ${data.country}`],
    data.round && ['Round', data.round],
    data.format && ['Format', data.format],
    ts.length && ['Length', `${ts.length} km`],
    ts.type && ['Type', ts.type],
    ts.first_gp && ['First GP', ts.first_gp],
    data.race_count && ['Races held', data.race_count],
    ts.lap_record_time && ['Lap record', `${ts.lap_record_time} — ${ts.lap_record_driver} (${ts.lap_record_year})`],
  ].filter(Boolean);

  return wrap(
    <>
      {data.circuit_map && (
        <img src={data.circuit_map} alt="Circuit map"
          style={{ maxWidth: '100%', maxHeight: '44vh', display: 'block', margin: '0 auto 14px', filter: 'grayscale(1) contrast(1.05)' }} />
      )}
      <div className="pv-info">
        {rows.map(([k, v], i) => (
          <div key={i} className="pv-info-row"><span className="pv-info-key">{k}</span><span className="pv-info-val">{v}</span></div>
        ))}
        {(data.sessions || []).map((s, i) => (
          <div key={i} className="pv-info-row"><span className="pv-info-key">{s.name}</span><span className="pv-info-val">{s.date}</span></div>
        ))}
      </div>
    </>
  );
};

const StatList = ({ title, stats }) => (
  <>
    <div className="pv-chart-sub" style={{ marginTop: 14 }}>{title}</div>
    <div className="pv-info">
      {stats.filter(([, v]) => v != null).map(([k, v], i) => (
        <div key={i} className="pv-info-row">
          <span className="pv-info-key">{k}</span>
          <span className="pv-info-val">{v}</span>
        </div>
      ))}
    </div>
  </>
);

export const DriverStatsCard = ({ data }) => {
  if (!data) return <p className="pv-stage-note">No data found for that driver.</p>;
  const c = data.career;
  return wrap(
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
        {data.headshot && (
          <img src={data.headshot} alt="" width="72" height="72"
            style={{ width: 72, height: 72, objectFit: 'cover', border: '1px solid var(--line-strong)', filter: 'grayscale(1)' }} />
        )}
        <div>
          <div style={{ fontSize: '1.1rem', fontWeight: 900, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--ink)' }}>
            {data.full_name || data.driver}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 4 }}>
            {data.number && <span style={{ fontSize: '1.4rem', fontWeight: 900, color: 'var(--accent)', fontVariantNumeric: 'tabular-nums' }}>{data.number}</span>}
            {data.team && <span style={{ fontSize: '0.68rem', letterSpacing: 1, textTransform: 'uppercase', color: 'var(--dim)' }}>{data.team}</span>}
          </div>
          {data.nationality && (
            <div style={{ fontSize: '0.64rem', color: 'var(--dim)', marginTop: 3 }}>
              {data.nationality}{data.dob ? ` · Born ${data.dob}` : ''}
            </div>
          )}
        </div>
      </div>

      {data.races > 0 && (
        <StatList title={`${data.year} season`} stats={[
          ['Races', data.races], ['Points', data.points], ['Wins', data.wins],
          ['Podiums', data.podiums], ['Fastest laps', data.fastest_laps],
          ['DNFs', data.dnfs], ['Avg grid', data.avg_grid], ['Avg finish', data.avg_finish],
        ]} />
      )}
      {c && c.races > 0 && (
        <StatList title={`Career${c.first_season && c.last_season ? ` (${c.first_season}–${c.last_season})` : ''}`} stats={[
          ['Seasons', c.seasons], ['Championships', c.championships],
          ['Races', c.races], ['Points', c.points], ['Wins', c.wins],
          ['Podiums', c.podiums], ['Fastest laps', c.fastest_laps],
          ['DNFs', c.dnfs], ['Avg grid', c.avg_grid], ['Avg finish', c.avg_finish],
        ]} />
      )}
    </>
  );
};

/* ═══ Dispatcher ═══════════════════════════════════════════════════════ */
export function renderResult(d) {
  if (!d) return null;
  switch (d.type) {
    case 'fastest': return <FastestChart data={d.data} pole_time={d.pole_time} pole_driver={d.pole_driver} />;
    case 'laps': return <LapsChart drivers={d.drivers} />;
    case 'time': return <SpeedChart drivers={d.drivers} xKey="time" xLabel="Time (s)" />;
    case 'distance': return <SpeedChart drivers={d.drivers} xKey="distance" xLabel="Distance (m)" />;
    case 'delta': return <DeltaChart data={d.data} driver1={d.driver1} driver2={d.driver2} />;
    case 'telemetry': return <TelemetryChart data={d.data} driver1={d.driver1} driver2={d.driver2} />;
    case 'positions': return <PositionsChart drivers={d.drivers} safety_car={d.safety_car} />;
    case 'strategy': return <StrategyChart drivers={d.drivers} compound_colors={d.compound_colors} />;
    case 'racetrace': return <RaceTraceChart drivers={d.drivers} />;
    case 'battles': return <BattlesChart data={d.data} />;
    case 'cornering': return <CorneringChart speed_data={d.speed_data} driver1={d.driver1} driver2={d.driver2} speed_text={d.speed_text} distance_range={d.distance_range} action_colors={d.action_colors} />;
    case 'results': return <DataTable columns={d.columns} rows={d.rows} column_keys={d.column_keys} />;
    case 'schedule': return <ScheduleTable events={d.events} />;
    case 'event': return <EventInfo fields={d.fields} />;
    case 'track': return <TrackInfo data={d.data} />;
    case 'driverstats': return <DriverStatsCard data={d.data} />;
    default: return null;
  }
}
